import React, { useContext, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useQuery } from '@apollo/client';
import CircularProgress from '@material-ui/core/CircularProgress';
import { loader } from 'graphql.macro';
import RecommendationServiceCarousel from './RecommendationServiceList';
import type { RecommendationServiceTypes } from './RecommendationServiceTypes';
import { ProductContext } from '../../../ProductContext';
import { getBrowseApiHeaders } from 'src/scripts/templates/search-page/SearchPageUtils/constants';
import { brRecommendationMetadataStore } from 'src/scripts/templates/_shared/apolloClientUtils';
import { ShippingCountryState } from 'src/scripts/utils/shipping';
import { manualActivationCheckIfInVariation } from 'src/scripts/utils/optimizely';
import {
  PRODUCTS_RECS_AB_TEST_EXPERIMENT_ID,
  PRODUCTS_RECS_AB_TEST_HEADERS,
  PRODUCTS_RECS_AB_TEST_PAGE_NAME,
  PRODUCTS_RECS_AB_TEST_VARIATION_ID,
  getBRRecommendationStoreKey,
} from 'src/scripts/utils/bloomreach-recommendations';

const RecommendedProductsQuery = loader(
  './../../../../_shared/ui-components/RecommendedProductsCarousel/GetRecommendedProducts.product-service.graphql'
);

const RecommendationService = ({
  productId,
  selectedSize,
  portalTargetId,
  recommendationScheme,
  blockTitle,
}: RecommendationServiceTypes) => {
  const variables = {
    productId: productId,
    first: 10,
    ...(selectedSize
      ? { sizeNameInStock: selectedSize, recommendationScheme: recommendationScheme }
      : { recommendationScheme: recommendationScheme }),
  };

  // products recs ab test - to be removed after experiment conclusion
  const productsRecTest = manualActivationCheckIfInVariation({
    variationId: PRODUCTS_RECS_AB_TEST_VARIATION_ID,
    experimentId: PRODUCTS_RECS_AB_TEST_EXPERIMENT_ID,
    pageName: PRODUCTS_RECS_AB_TEST_PAGE_NAME,
  });

  const { setSimilarItemsCarouselExists } = useContext(ProductContext);
  const { loading, error, data } = useQuery(RecommendedProductsQuery, {
    variables,
    context: {
      headers: {
        ...getBrowseApiHeaders(),
        ...(productsRecTest ? PRODUCTS_RECS_AB_TEST_HEADERS : {}),
      },
    },
  });

  // products recs ab test - to be removed after experiment conclusion
  const key = getBRRecommendationStoreKey(recommendationScheme, variables, ShippingCountryState.countryCode);
  const brRecommendationMetadata = brRecommendationMetadataStore.get(key);

  if (error) {
    console.warn(error);
    // @ts-expect-error
    if (window.Shopify.theme.role === 'main' && window.newrelic) window.newrelic.noticeError(error);
  }

  const targetContainer: HTMLElement | null = document.getElementById(portalTargetId);

  const loadingState = (
    <div className="loading__block mt-5 pt-md-5 mb-5 pb-md-5">
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="inherit" />
      </div>
      <p className="large-p mt-2">Loading...</p>
    </div>
  );
  useEffect(() => {
    if (portalTargetId === 'SimilarItems') {
      setSimilarItemsCarouselExists(!!data?.recommendedProductsByProductId?.length);
    }
  }, [setSimilarItemsCarouselExists, data, portalTargetId]);

  if (loading && targetContainer) {
    return ReactDOM.createPortal(loadingState, targetContainer);
  }
  if ((data?.recommendedProductsByProductId?.length === 0 || error) && targetContainer)
    targetContainer.style.minHeight = 'initial';

  return data?.recommendedProductsByProductId?.length > 0 && targetContainer
    ? ReactDOM.createPortal(
        <RecommendationServiceCarousel
          data={data}
          blockTitle={blockTitle}
          brRecommendationMetadata={brRecommendationMetadata}
        />,
        targetContainer
      )
    : null;
};

export { RecommendationService };
